// ui/src/services/reembolsoApi.ts
import api from '@/config/axios';
import type { IntegrationTestResult } from '@/types/integrationTest';

export type ReembolsoStatus = 'rascunho' | 'conferencia' | 'novo' | 'em_analise' | 'aprovado' | 'pago' | 'reprovado' | 'cancelado';

export interface RateioLinha {
  cc_codigo: string | null;
  itconta_codigo: string | null;
  percentual: number;
  /** Nome amigável do centro de custo (Rev8 §14.4.1); null quando não resolve. */
  cc_nome?: string | null;
  /** Nome amigável do item de conta (Rev8 §14.4.1); null quando não resolve. */
  itconta_nome?: string | null;
}

export interface CodigoNome {
  codigo: string;
  nome: string;
}

export type CampoIa = 'valor' | 'data' | 'categoria' | 'moeda';

export interface Despesa {
  id?: number;
  /** Só no PAYLOAD da linha nova: a chave local `_k`. Transitória (não é gravada); volta em `chaves_criadas`. */
  cliente_chave?: string;
  tipo: 'geral' | 'km';
  data_despesa?: string | null;
  moeda?: 'BRL' | 'USD' | 'EUR' | 'GBP';
  categoria?: 'alimentacao' | 'hospedagem' | 'transporte' | 'pedagio' | 'outros' | 'loc_automovel' | 'pass_aerea' | 'transp_terrestre' | 'capa_pelic_cel' | 'cursos' | 'serv_inf' | 'mat_esc' | 'estacionamento' | 'manut_veic' | 'telefone' | null;
  subtipo?: string | null;        // <-- ADDED (backend has this field)
  descricao?: string | null;
  valor?: number;
  acima_padrao?: boolean;
  limite_aplicado?: number | null;
  origem?: string | null;
  destino?: string | null;
  km_inicial?: number | null;
  km_final?: number | null;
  km_rodado?: number | null;
  tipo_veiculo?: 'combustao' | 'eletrico' | 'hibrido' | null;
  valor_km?: number | null;
  pedagio_valor?: number | null;
  ida_volta?: boolean;
  km_volta?: number | null;
  criada_em?: string | null;
  comprovante_url?: string | null;
  comprovante_key?: string | null;
  /** Central do financeiro: quem/quando conferiu. Limpa sozinho quando a despesa é editada. */
  conferida_por_id?: number | null;
  conferida_em?: string | null;
  rateio?: RateioLinha[];
  /** Leitura: o backend serializa o include Sequelize como 'rateios' (plural). */
  rateios?: RateioLinha[];
  /** Leitura por IA (spec §4A): dados de apoio, editáveis. */
  estabelecimento_cnpj?: string | null;
  estabelecimento_nome?: string | null;
  /** 'lendo' = a IA ainda está lendo o comprovante; 'lida'/'falhou' = terminou (falha é silenciosa). */
  ia_status?: 'lendo' | 'lida' | 'falhou' | null;
  /** Campos que a IA preencheu e ainda têm a marca (cai ao editar o campo ou ao enviar). NÃO afeta "Pronta". */
  ia_campos?: CampoIa[];
  /** JSON (string) da leitura completa; `valor_nao_preenchido` = lido, mas sem cotação para preencher (ver `reembolsoAvisoIa`). */
  ia_leitura?: string | null;
}

export interface Pagamento {
  tipo: 'pix' | 'ted';
  pix_tipo_chave?: 'cpf' | 'cnpj' | 'email' | 'telefone' | 'aleatoria' | null;
  pix_chave?: string | null;
  /** `true` = o servidor já mascarou `pix_chave`: exibir como veio, sem remascarar. */
  pix_chave_mascarada?: boolean;
  banco?: string | null;
  agencia?: string | null;
  conta?: string | null;
  digito?: string | null;
}

/** §12.2 — estatísticas do solicitante embutidas no detalhe (aprovador/conferente). */
export interface SolicitanteStats {
  pedidos_ano: number;
  valor_medio: number | null;
  taxa_aprovacao: number | null; // 0..1
  ultimo_pedido_em: string | null; // ISO date
}

/** §12.2 — dados do solicitante (card do topo do detalhe v2). */
export interface SolicitanteInfo {
  nome: string | null;
  email: string | null;
  department: string | null;
  company: string | null;
}

export interface Reembolso {
  id: number;
  numero: string;
  solicitante_id: number;
  departamento?: string | null;
  unidade?: string | null;
  adiantamento: number;
  /** Título do PA (adiantamento Protheus) consumido nesta solicitação. */
  pa_titulo?: string | null;
  /** Valor do título PA no momento da seleção. */
  pa_valor?: number | null;
  subtotal: number;
  total_final: number;
  status: ReembolsoStatus;
  aprovador_id?: number | null;
  nivel_alcada?: number | null;
  nivel_atual?: number | null;
  nivel_maximo?: number | null;
  observacao?: string | null;
  /** Observação geral do pedido, preenchida pelo solicitante (distinta de `observacao`). */
  observacao_solicitante?: string | null;
  aprovado_em?: string | null;
  veiculo_modelo?: string | null;
  veiculo_placa?: string | null;
  created_at?: string;
  despesas?: Despesa[];
  /**
   * Só na resposta de create/update do rascunho: `{ cliente_chave: id }` das despesas criadas NESTA
   * requisição. O servidor manda ao lado de `data`; `reembolsoApi` junta aqui.
   */
  chaves_criadas?: Record<string, number>;
  /**
   * As cotações com que `subtotal`/`total_final` GRAVADOS foram calculados (coluna do pedido desde a 9ª revisão; a 10ª
   * rodada a criou só na resposta do criar/PUT). Vem no GET por id, no criar/PUT, na troca de comprovante e nos polls —
   * a tela soma com elas as moedas que o contexto do formulário não trouxe. `[]` = nenhuma moeda estrangeira com valor;
   * `null`/ausente = total gravado antes da coluna existir.
   */
  cotacoes_usadas?: CotacaoUsadaNoTotal[] | null;
  pagamento?: Pagamento | null;
  protheus_status?: 'pendente' | 'enviado' | 'erro' | null;
  protheus_documento?: string | null;
  protheus_tenant?: 'via' | 'sim' | null;
  protheus_cambio?: number | null;
  protheus_erro?: string | null;
  /**
   * Spec 2026-08-31 — janela-alvo de envio ao ERP, gravada na submissão e nunca
   * recalculada. 'YYYY-MM-DD' (DATEONLY) ou null quando a config está desligada
   * (sem retenção).
   */
  janela_envio_em?: string | null;
  /**
   * Spec 2026-08-31 (Task 8) — janela EFETIVA de envio (pode diferir de
   * `janela_envio_em` quando o pedido "rolou" para a janela seguinte).
   * 'YYYY-MM-DD' (DATEONLY) ou null quando não há retenção em curso.
   */
  janela_efetiva_em?: string | null;
  /**
   * Calculado no backend a partir do papel de quem consulta, do status, de a
   * seção estar habilitada e de a janela efetiva ainda estar no futuro — nunca
   * re-derive esta regra no front (lição da Task 8: o caso `tomDoCstat` do
   * módulo Fiscal TSS).
   */
  pode_antecipar_janela?: boolean;
  /** §12.2 — presentes no detalhe de aprovador/conferente. */
  solicitante_stats?: SolicitanteStats | null;
  solicitante_info?: SolicitanteInfo | null;
  /**
   * §12.2 — cadeia de aprovação DO PEDIDO (resolvida pelo solicitante, não pelo
   * visualizador), embutida no detalhe de aprovador/conferente.
   */
  cadeia?: PreviewCadeia | null;
  /**
   * Rev7 §13.1 — gate do botão "Marcar como pago" (só no detalhe de aprovador/
   * conferente; o endpoint do solicitante não emite este campo).
   */
  pode_pagar?: boolean;
  /** Detalhe: o usuário que pediu é o dono do pedido (decidido no servidor). */
  sou_solicitante?: boolean;
  /** Detalhe: este usuário edita o pedido pelo formulário (mesma regra do PUT, decidida no servidor). */
  pode_editar?: boolean;
  /** Detalhe: decisões do ciclo atual (conferência e cada nível), com o texto escrito. */
  decisoes?: DecisaoDetalhe[];
  /** Detalhe: quando o solicitante enviou o pedido neste ciclo. */
  enviado_em?: string | null;
  /** Detalhe: aprovador da vez (só em novo/em_analise). */
  aprovador_atual?: { id: number; nome: string } | null;
  /** Nome da empresa do ERP (snapshot normalizado). Nunca exibir `protheus_empresa` (código de filial). */
  protheus_empresa_nome?: string | null;
}

/**
 * Decisão de uma etapa do pedido (detalhe da solicitação): conferência ou um nível de aprovação.
 * `texto` = a observação/justificativa escrita (sem o prefixo "[motivo]" da reprovação).
 */
export interface DecisaoDetalhe {
  tipo: 'conferido' | 'aprovado' | 'reprovado';
  etapa: 'conferencia' | 'alcada';
  nivel: number | null;
  papel: string;
  ator_id: number;
  ator_nome: string;
  em: string;
  texto: string | null;
  motivo: string | null;
  /** Quantas despesas acima do padrão a decisão justificou (null em registros antigos). */
  acima_padrao_qtd: number | null;
}

export interface Kpis { total: number; em_analise: number; aprovados: number; total_reembolsado: number; }
export interface ListaResult { items: Reembolso[]; kpis: Kpis; }

/** Rev7 §13.2 — progresso da cadeia (dots) de um pedido na fila unificada. */
export interface EtapaFila { done: number; ativo: boolean; total: number; reprovado: boolean }

/** Rev7 §13.2 — item da fila unificada (conferente/aprovador). */
export interface FilaItem {
  id: number; numero: string; status: ReembolsoStatus; created_at: string;
  solicitante: { id: number; nome: string; departamento: string | null; photo_url: string | null };
  total_final: number; adiantamento: number;
  /**
   * Rascunho com total calculado por PTAX PROVISÓRIA (de dia anterior, do cache: o BCB não respondeu). O envio recalcula.
   * Derivado da coluna `cotacoes_usadas` pelo servidor (9ª revisão). Ausente = servidor antigo = não provisório.
   */
  total_provisorio?: boolean;
  tem_acima_padrao: boolean; categorias: string[]; n_comprovantes: number;
  /**
   * Despesas acima do padrão (só elas), para o popup de aprovar/conferir PELA LINHA
   * já abrir com a justificativa obrigatória e dizer por quê — o servidor recusa a
   * aprovação sem observação ≥ 10 caracteres quando há despesa acima do limite.
   */
  acima_padrao_itens: AcimaPadraoItem[];
  protheus_status: 'pendente' | 'enviado' | 'erro' | null; protheus_documento: string | null;
  pode_pagar: boolean; aguardando_voce: boolean; etapa: EtapaFila;
  /** Rev8 — a solicitação é do próprio usuário (aba "Minhas"). */
  sou_solicitante: boolean;
  /** Rev8 — o usuário é o aprovador do nível atual e o pedido aguarda sua decisão (aba "Para aprovar" + ações inline). */
  sou_aprovador_atual: boolean;
  /**
   * Fix de visibilidade (2026-09-06, 2ª rodada) — janela EFETIVA de envio ao
   * ERP. AUTORIDADE NO BACKEND: `null` significa "não retido" — seção
   * desligada, sem janela gravada, já enviado, OU falha real de integração
   * diagnosticada (`estaRetidoPelaJanela`, `reembolso.service.ts`). O front só
   * pergunta "veio data?" para decidir o marcador ao lado do `StatusPill` — nunca
   * re-derive a regra aqui (a 1ª versão deste campo comparava só a data no
   * componente e reintroduziu o Finding 1: falha real de integração se
   * disfarçando de retenção saudável).
   *
   * Por que o front NÃO consegue re-derivar, com precisão (o review do #375 pegou
   * a versão anterior deste texto dizendo que `protheus_status` "nunca viaja
   * neste DTO" — viaja, é obrigatório logo acima): `protheus_status` vem sempre,
   * mas `protheus_erro` vem REDIGIDO para `null` a quem não é conferente
   * (aprovador e solicitante). Para esse visualizador, `null` significa tanto
   * "sem erro" quanto "há erro e você não vê" — então `!protheus_erro` aqui não
   * prova nada, e a retenção não pode ser recomposta no cliente. Só o backend,
   * com o valor real em mãos, decide.
   *
   * Este campo, `pode_antecipar_janela` e `protheus_erro` são OBRIGATÓRIOS (não
   * `?`): `listarFilaUnificada` emite os três em toda linha, sempre. Marcá-los
   * opcionais deixaria o TypeScript aceitar um payload que os omitisse — a
   * regressão silenciosa que o contrato existe para acusar.
   *
   * ⚠ INVARIANTE (review do #375): este campo e `pode_antecipar_janela` saem da
   * MESMA função no backend. Logo `pode_antecipar_janela === true` implica
   * `janela_efetiva_em !== null`, e é isso que torna seguro interpolar a data no
   * tooltip do botão da fila. Antes eles tinham réguas diferentes e o tooltip
   * saía "sem esperar " — com a data vazia — justamente no pedido quebrado.
   */
  janela_efetiva_em: string | null;
  /**
   * O visualizador pode enviar ESTE pedido ao ERP fora da janela. Calculado no
   * backend pela MESMA função do detalhe (papel de conferente, não ser o
   * solicitante, status aprovado, seção habilitada, janela ainda futura) — fila e
   * detalhe não podem divergir sobre quem pode agir.
   */
  pode_antecipar_janela: boolean;
  /**
   * Motivo de uma pendência de integração, **só para conferente** (gate =
   * `ehConferente`, e nada mais — NÃO é o recorte de `pode_pagar`, que exige
   * `ehConferente && podePagar(...)`, nem o de `pode_antecipar_janela`: um
   * conferente que é o próprio solicitante RECEBE o erro e ainda assim não pode
   * antecipar). O papel basta porque o conferente precisa do motivo justamente
   * quando o pedido NÃO pode ser pago. Alimenta o toast da fila depois de
   * "Enviar ao ERP", que sem ele só saberia dizer o genérico "não foi possível
   * confirmar". `null` para quem não é conferente — e `null` também quando não há
   * erro nenhum, então ele nunca sozinho significa "falhou".
   */
  protheus_erro: string | null;
}

/** Rev7 §13.2 — KPIs da fila unificada. */
export interface AcimaPadraoItem {
  categoria: string | null; subtipo: string | null; valor: number;
  moeda: 'BRL' | 'USD' | 'EUR' | 'GBP'; limite_aplicado: number | null;
}
export interface FilaKpis { aguardando_voce: number; em_andamento: number; aprovados_mes: number; reprovados_mes: number }

export interface AdiantamentoPA {
  empresa: string;
  titulo: string;
  fornecedor: string;
  nome: string;
  valor: number;
  cgc: string;
}

export interface CreateReembolsoPayload {
  /** Remoção EXPLÍCITA: despesa com comprovante só é destruída quando o id está aqui. */
  despesas_removidas?: number[];
  enviar?: boolean;
  adiantamento?: number;
  pa_titulo?: string | null;
  pa_valor?: number | null;
  veiculo_modelo?: string | null;
  veiculo_placa?: string | null;
  observacao_solicitante?: string | null;
  despesas: Despesa[];
  pagamento?: Pagamento;
}

export interface AlcadaConfig {
  nivel: number; nome: string; valor_min: number; valor_max: number | null;
  resolve_por: 'departamento_supervisor' | 'departamento_gestor' | 'departamento_diretor' | 'usuario_fixo';
  aprovador_user_id: number | null;
}
export interface DepartamentoConfig {
  department_name: string;
  /** Conferente PRÓPRIO do departamento, acionado antes do supervisor. Sem valor, cai no pool padrão de conferentes. */
  conferente_user_id: number | null;
  supervisor_user_id: number | null;
  gestor_user_id: number | null;
  diretor_user_id: number | null;
  ativo?: boolean;
}
export interface ConfigLinha { id?: number; moeda?: 'BRL' | 'USD' | 'EUR' | 'GBP'; tipo: string; limite?: number; valor_km?: number; }
export interface EmailSettings {
  notifications_paused: boolean;
  /** Disparos novos ao solicitante (ausente = ligado). */
  notify_enviado?: boolean;
  notify_conferido?: boolean;
  notify_lancado_protheus?: boolean;
  notify_cancelado?: boolean;
  /** Destinatários do aviso de pendência do ERP (migration 364); vazio/null = admins da configuração. */
  email_pendencia_erp?: string | null;
  updated_at?: string | null;
  updated_by?: number | null;
}
/** Regra 1 — config GLOBAL da conferência: liga/desliga + antes/depois das alçadas. */
export interface ConferenciaConfig {
  habilitada: boolean;
  quando: 'antes' | 'depois';
}
/** Regra 2 — números para o aviso da aba Departamentos (lista é derivada dos CCs mapeados). */
export interface DepartamentosCcInfo {
  mapeados: number;
  total: number;
}
/** Janelas de envio ao ERP (spec 2026-08-31): um interruptor POR janela
 * (migration 365) + os quatro dias de corte/envio, singleton por tenant. O
 * backend exige corte < envio em cada janela ligada e, com as duas ligadas,
 * envio1 < corte2. `habilitada` é derivada (janela1 OR janela2): o GET a
 * devolve, o PUT a ignora quando as flags por janela vêm. */
export interface JanelasEnvioConfig {
  habilitada: boolean;
  janela1_habilitada: boolean;
  janela2_habilitada: boolean;
  janela1_corte_dia: number;
  janela1_envio_dia: number;
  janela2_corte_dia: number;
  janela2_envio_dia: number;
}
export interface ReembolsoConfig {
  alimentacao: ConfigLinha[];
  hospedagem: ConfigLinha[];
  km: ConfigLinha[];
  alcadas: AlcadaConfig[];
  departamentos: DepartamentoConfig[];
  diretrizes: { elegivel?: string | null; nao_elegivel?: string | null } | null;
  email_settings?: EmailSettings;
  /** Rotear alçada pelo centro de custo predominante do rateio (em vez do departamento do solicitante). */
  alcada_por_cc?: boolean;
  /** §12.1 — pool de conferentes do financeiro (apenas ativos). */
  conferentes?: Array<{ user_id: number; nome: string; ativo: boolean }>;
  /** Regra 1 — liga/desliga + antes/depois das alçadas (singleton por tenant). */
  conferencia_config?: ConferenciaConfig;
  /** Regra 2 — quantos CCs estão mapeados vs. o total (aviso da lista curta de departamentos). */
  departamentos_cc_info?: DepartamentosCcInfo;
  /** Janelas de envio ao ERP — represa o pedido aprovado até a próxima janela configurada. */
  janelas_config?: JanelasEnvioConfig;
}
/** §12 — payload de PUT /configuracoes: `conferentes` vai como lista de user_id. */
export type SaveConfigPayload = Omit<Partial<ReembolsoConfig>, 'conferentes'> & { conferentes?: number[] };

export interface PreviewAlcada { nivel: number; nome?: string; nivel_maximo?: number; aprovador: { id: number; nome: string } | null; }

/** §11.6 — etapa da cadeia de aprovação resolvida (widget de steps). */
export interface PreviewCadeiaEtapa {
  nivel: number;
  nome?: string | null;
  aprovador: { id: number; nome: string } | null;
  atual: boolean;
  pulado: boolean;
}
export interface PreviewCadeia {
  nivel_atual: number | null;
  nivel_maximo: number;
  /** §12 — há conferentes ativos? Quando true, `etapas` recebe a etapa 0 "Conferência". */
  conferencia?: boolean;
  /**
   * Estado REAL do pedido em conferência: 'antes' (as alçadas ainda não rodaram) ou 'depois'
   * (já rodaram). `nivel_atual` não distingue — no 'antes' ele é o 1º aprovador PREVISTO.
   */
  modo_conferencia?: 'antes' | 'depois' | null;
  etapas: PreviewCadeiaEtapa[];
}

/** §11.3 — cotação de moeda estrangeira → BRL do dia (exibição no formulário). */
export interface CotacaoUsd { valor: number; fonte: 'ptax' | 'manual'; }
export type MoedaEstrangeira = 'USD' | 'EUR' | 'GBP';
/**
 * Cotação que pesou no total gravado do rascunho (`cotacoes_usadas`): o número que entrou no total; `dia` (AAAA-MM-DD)
 * = o dia do boletim PTAX usado, ou, na manual, o dia em que o servidor a leu; `provisoria` = PTAX de dia anterior
 * porque o BCB não respondeu (o envio recalcula).
 */
export interface CotacaoUsadaNoTotal {
  moeda: MoedaEstrangeira; cotacao: number; dia: string; provisoria: boolean; fonte: 'ptax' | 'manual';
}

export interface LimiteMoeda { max: number; tipos?: Record<string, number>; }
export type LimitesPorCategoria = Partial<Record<string, Partial<Record<'BRL' | 'USD' | 'EUR' | 'GBP', LimiteMoeda>>>>;

export interface PreferenciaUsuario {
  veiculo_modelo?: string | null;
  veiculo_placa?: string | null;
  pagamento?: Pagamento | null;
}

export interface ContextoFormulario {
  km: Array<{ tipo: string; valor_km: number }>;
  diretrizes: { elegivel: string; nao_elegivel: string };
  limites: LimitesPorCategoria;
  /** Rateio (CC + item conta) obrigatório no envio — só quando a integração Protheus está habilitada. */
  rateioObrigatorio: boolean;
  /** Leitura do comprovante por IA ligada e liberada para este tenant. */
  leituraIaAtiva?: boolean;
  /** §11.3 — cotação USD→BRL do dia; null quando indisponível/não configurado. */
  cotacaoUsd: CotacaoUsd | null;
  /** Item 8b — cotação (+ fonte) de cada moeda estrangeira do dia (USD/EUR/GBP). */
  cotacoes?: Partial<Record<MoedaEstrangeira, CotacaoUsd>>;
  preferencia?: PreferenciaUsuario | null;
  tenant: 'via' | 'sim';
  tenantAviso: string | null;
  ccOptions: CodigoNome[];
  itemContaOptions: CodigoNome[];
  ccPadrao: string | null;
  /** §11.2 — item conta padrão ("CORPORATIVO") do tenant do usuário; null se ausente. */
  itemPadrao: string | null;
}

export interface HistoricoEntry {
  id: number;
  solicitacao_id: number;
  acao: string;
  ator_id: number;
  ator_nome: string;
  nivel: number | null;
  detalhes: Record<string, unknown> | null;
  created_at: string;
}

export interface Delegacao {
  id: number;
  delegante_user_id: number;
  delegado_user_id: number;
  /** Nome/e-mail resolvidos pela API (listagem); ausentes na resposta de criar/desativar. */
  delegante_nome?: string | null;
  delegante_email?: string | null;
  delegado_nome?: string | null;
  delegado_email?: string | null;
  data_inicio: string;
  data_fim: string;
  motivo: string | null;
  criado_por: number;
  ativo: boolean;
  created_at: string;
}

export interface CreateDelegacaoPayload {
  delegante_user_id: number;
  delegado_user_id: number;
  data_inicio: string;
  data_fim: string;
  motivo?: string | null;
}

/** §11.5 — token temporário para upload de comprovante via QR Code. */
export interface QrTokenComprovante {
  token: string;
  expiresAt: string;
  uploadUrl: string;
}

/** §11.5 — status do comprovante da despesa (polling do modal QR). */
export interface StatusComprovante {
  tem: boolean;
  comprovante_url: string | null;
  /**
   * Estado ATUAL das colunas que a troca de comprovante pode gravar (arquivo, "Conferida", marcas/leitura da IA,
   * valor/moeda/data/categoria, estabelecimento/CNPJ). O desktop que espera o QR aplica isto na linha.
   */
  linha?: Partial<Despesa>;
  /** As cotações do total gravado do pedido (9ª revisão): a troca pelo celular pode tê-lo regravado. */
  cotacoes_usadas?: CotacaoUsadaNoTotal[];
}

/** Resposta da troca/anexo unitário: `alterados` = o que a troca gravou na linha (o cliente aplica, não deduz). */
export interface ComprovanteAnexado {
  url: string;
  key: string;
  alterados?: Partial<Despesa>;
  /** As cotações do total do pedido DEPOIS desta troca (9ª revisão). */
  cotacoes_usadas?: CotacaoUsadaNoTotal[];
}

/** Rev7 §13.3 — uma etapa da cadeia de custódia exibida no comprovante final (PDF). */
export interface EtapaComprovante {
  ordem: number;
  papel: string;
  ator_nome: string;
  em: string;
  hash: string;
}

/** Rev7 §13.3 — payload do comprovante final (só disponível em status `pago`). */
export interface ComprovanteData {
  solicitacao: Reembolso;
  hash: string;
  cadeia: EtapaComprovante[];
  emitido_em: string;
}

// ===== Central do financeiro (spec 2026-10-08 §5) =====
export type AbaCentral = 'conferir' | 'aguardando_erp' | 'pagar' | 'pagos' | 'problema';
export type PeriodoCentral = 'todos' | '7d' | '30d' | 'mes';

/** Empresa: SÓ o nome. O código de filial nunca chega ao front. */
export interface EmpresaCentral {
  nome: string | null;
  instancia: 'via' | 'sim' | null;
  /** "Por que esta empresa?" — só quando a resolução é ao vivo (pedido ainda não enviado). */
  motivo: string | null;
  definida: boolean;
  pendencia: string | null;
  pendencia_tipo: 'sem_vinculo' | 'vinculo_duplo' | 'sem_cgc' | 'empresa_indefinida' | null;
}

export type AcaoProblema = 'reenviar' | 'ver_erro' | 'abrir_identidade' | 'marcar_pago';
export interface ProblemaCentral {
  tipo: 'erro_envio' | 'sem_vinculo' | 'vinculo_duplo' | 'sem_cgc' | 'empresa_indefinida' | 'pendencia';
  titulo: string;
  detalhe: string;
  acoes: AcaoProblema[];
}

export interface ItemCentral {
  id: number;
  numero: string;
  status: ReembolsoStatus;
  sou_solicitante: boolean;
  solicitante: { id: number; nome: string };
  departamento: string | null;
  empresa: EmpresaCentral;
  total_final: number;
  n_despesas: number;
  n_acima_padrao: number;
  recebido_em: string | null;
  aprovado_em: string | null;
  pago_em: string | null;
  protheus_status: 'pendente' | 'enviado' | 'erro' | null;
  protheus_documento: string | null;
  protheus_erro: string | null;
  protheus_tentativas: number;
  ultima_tentativa_em: string | null;
  /** Backend decide (mesma régua do detalhe): `null` = não retido. O front NÃO recalcula. */
  janela_efetiva_em: string | null;
  pode_antecipar_janela: boolean;
  pode_pagar: boolean;
  /** Servidor decide: só admin da configuração do Reembolso. O front NÃO recalcula. */
  pode_reenviar: boolean;
  pagamento: { tipo: 'pix' | 'ted'; pix_tipo_chave: 'cpf' | 'cnpj' | 'email' | 'telefone' | 'aleatoria' | null } | null;
  problema: ProblemaCentral | null;
}

export interface FilaCentral {
  aba: AbaCentral;
  items: ItemCentral[];
  total: number;
  pagina: number;
  total_paginas: number;
  tamanho: number;
  contadores: Record<AbaCentral, { qtd: number; valor: number }>;
  departamentos: string[];
  empresas: string[];
  tem_empresa_nao_definida: boolean;
  abas_visiveis: AbaCentral[];
  integracao_ativa: boolean;
  /** Algum grupo bateu no teto (ver `truncadas`, que diz QUAL). Opcional por compatibilidade com respostas antigas. */
  truncada?: boolean;
  /** Um item por grupo cortado: as abas afetadas e qual ponta ficou (`mais_antigos` = os mais RECENTES foram cortados). */
  truncadas?: Array<{ abas: AbaCentral[]; mantidos: 'mais_antigos' | 'mais_recentes' }>;
  /** Teto POR GRUPO (conferência, aprovados, pagos do mês). */
  teto?: number;
  /** Com `pedido=` na requisição: se o pedido está na lista filtrada da aba. `null`/ausente sem `pedido`. */
  pedido_na_lista?: boolean | null;
}

export interface FiltrosFilaCentral {
  aba: AbaCentral;
  periodo?: PeriodoCentral;
  empresa?: string;
  departamento?: string;
  busca?: string;
  pagina?: number;
  tamanho?: number;
  /** Link direto para um pedido: a resposta vem na página que o contém (`pagina` corrigida). */
  pedido?: number;
}

export interface ResultadoPagarLote { id: number; numero?: string; ok: boolean; erro?: string; status?: number }

export interface ResultadoLote { nome: string; despesa?: Despesa; erro?: string }
export interface EmpresaPreview {
  nome: string | null;
  instancia: 'via' | 'sim' | null;
  motivo: string;
  pendencia?: string;
}

/**
 * Fase 2 da Identidade (spec 2026-10-15): o cadastro para o ERP está completo? Mesma checagem do envio
 * (`FUNCIONARIO_CLT`/`FORNECEDOR_PJ`; `USUARIO_PROTHEUS` não conta). `exigido=false` = o envio ao ERP não se
 * aplica ao tenant (integração desligada) e nada bloqueia.
 */
export type MotivoCadastroErp = 'sem_vinculo' | 'vinculo_duplo' | 'sem_cgc' | 'empresa_indefinida';
export interface VerificacaoCadastroErp {
  exigido: boolean;
  completo: boolean;
  motivo: MotivoCadastroErp | null;
  mensagem: string | null;
}

export interface IaConfigView {
  habilitada: boolean;
  modelo: string;
  orcamento_usd: number | null;
  preco_entrada_usd_mtok: number;
  preco_saida_usd_mtok: number;
  email_aviso: string | null;
  chave_configurada: boolean;
  precisao: { pct: number | null; amostra: number | null; em: string | null; meta: number; amostra_minima: number };
  gasto_mes_usd: number;
  leituras_mes: number;
  pct_orcamento: number;
  faixa: 'sem_orcamento' | 'ok' | 'aviso' | 'esgotado';
  pausada: { motivo: 'credito' | 'chave' | 'limite'; retoma_em: string | null } | null;
  porta: { ok: boolean; motivo: string | null };
  banner: { nivel: 'erro' | 'aviso'; texto: string } | null;
  cnae_mapa: Array<{ cnae_prefixo: string; categoria: string }>;
}
export interface SaveIaConfigPayload {
  habilitada?: boolean;
  modelo?: string;
  orcamento_usd?: number | null;
  preco_entrada_usd_mtok?: number;
  preco_saida_usd_mtok?: number;
  email_aviso?: string | null;
  cnae_mapa?: Array<{ cnae_prefixo: string; categoria: string }>;
}
/** `cotacoes_usadas` (9ª revisão): as do total gravado — o worker o regrava ao preencher valor/moeda. */
export interface StatusLeituraIa { lendo: number; despesas: Despesa[]; cotacoes_usadas?: CotacaoUsadaNoTotal[] }

export const reembolsoApi = {
  // Colaborador
  list: (params?: { status?: string; busca?: string }) =>
    api.get('/reembolso', { params }).then((r) => r.data.data as ListaResult),
  getById: (id: number) => api.get(`/reembolso/${id}`).then((r) => r.data.data as Reembolso),
  /** Títulos de adiantamento (PA) do usuário logado — consulta live no Protheus. */
  getAdiantamentos: () =>
    api.get('/reembolso/adiantamentos').then((r) => r.data as { adiantamentos: AdiantamentoPA[]; motivo?: string }),
  create: (payload: CreateReembolsoPayload) => api.post('/reembolso', payload)
    .then((r) => ({ ...r.data.data, chaves_criadas: r.data.chaves_criadas ?? {} }) as Reembolso),
  update: (id: number, payload: CreateReembolsoPayload) => api.put(`/reembolso/${id}`, payload)
    .then((r) => ({ ...r.data.data, chaves_criadas: r.data.chaves_criadas ?? {} }) as Reembolso),
  enviar: (id: number) => api.patch(`/reembolso/${id}/enviar`).then((r) => r.data.data as Reembolso),
  cancelar: (id: number) => api.patch(`/reembolso/${id}/cancelar`).then((r) => r.data.data as Reembolso),
  /** Exclui um rascunho (hard delete) — só o solicitante, só em `rascunho`. Diferente de `cancelar`. */
  excluir: (id: number) => api.delete(`/reembolso/${id}`).then((r) => r.data.data as { id: number }),
  uploadComprovante: (id: number, despesaId: number, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/reembolso/${id}/despesas/${despesaId}/comprovante`, fd).then((r) => r.data.data as ComprovanteAnexado);
  },
  /** Busca o comprovante via API autenticada (R2 privado não aceita link direto) — consumido pelo ModalVisualizarComprovante. */
  fetchComprovanteBlob: async (solicitacaoId: number, despesaId: number): Promise<Blob> => {
    const response = await api.get(
      `/reembolso/${solicitacaoId}/despesas/${despesaId}/comprovante/arquivo`,
      { responseType: 'blob' },
    );
    return response.data as Blob;
  },

  // §11.5 — upload de comprovante via QR Code (modal + polling)
  gerarQrTokenComprovante: (solicitacaoId: number, despesaId: number) =>
    api.post(`/reembolso/${solicitacaoId}/despesas/${despesaId}/comprovante/qr-token`).then((r) => r.data.data as QrTokenComprovante),
  statusComprovante: (solicitacaoId: number, despesaId: number) =>
    api.get(`/reembolso/${solicitacaoId}/despesas/${despesaId}/comprovante/status`).then((r) => r.data.data as StatusComprovante),

  // Lançamento "comprovantes primeiro" (spec 2026-10-08 §4.2)
  uploadComprovantesLote: (id: number, files: File[]) => {
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    return api.post(`/reembolso/${id}/comprovantes`, fd).then((r) => r.data.data as ResultadoLote[]);
  },
  gerarQrLote: (id: number) =>
    api.post(`/reembolso/${id}/comprovantes/qr-token`).then((r) => r.data.data as QrTokenComprovante),
  statusLote: (id: number, desde: string) =>
    api.get(`/reembolso/${id}/comprovantes/status`, { params: { desde } })
      .then((r) => r.data.data as { despesas: Despesa[]; agora: string }),
  /** Com `solicitacaoId`: a empresa do SOLICITANTE daquele pedido (conferente editando pedido alheio). */
  empresaPreview: (solicitacaoId?: number | null) => api.get('/reembolso/empresa-preview', { params: solicitacaoId ? { solicitacao: solicitacaoId } : undefined })
    .then((r) => r.data.data as EmpresaPreview),
  /**
   * Fase 2: verifica o cadastro ERP de quem vai abrir uma solicitação nova (e põe/tira da fila "Aguardando
   * cadastro"). Lê SÓ o envelope `{ data }` do resto do módulo (`r.data.data`): o controller que devolver o
   * corpo cru faz chegar `undefined` aqui, e a tela deixa de bloquear.
   */
  verificarCadastroErp: () => api.post('/reembolso/cadastro-erp/verificar')
    .then((r) => r.data.data as VerificacaoCadastroErp),

  // Leitura do comprovante por IA (spec §4A)
  getIaConfig: () => api.get('/reembolso/ia/config').then((r) => r.data.data as IaConfigView),
  saveIaConfig: (p: SaveIaConfigPayload) =>
    api.put('/reembolso/ia/config', p).then((r) => ({ view: r.data.data as IaConfigView, avisos: (r.data.avisos ?? []) as string[] })),
  retomarIa: () => api.post('/reembolso/ia/retomar').then((r) => r.data.data as IaConfigView),
  statusLeituraIa: (id: number) => api.get(`/reembolso/${id}/despesas/ia`).then((r) => r.data.data as StatusLeituraIa),
  limparLeituraIa: (id: number, despesaId: number) =>
    api.post(`/reembolso/${id}/despesas/${despesaId}/ia/limpar`).then(() => undefined),

  comprovante: (id: number) => api.get(`/reembolso/${id}/comprovante`).then((r) => r.data.data as ComprovanteData),

  // previewAlcada é GET com querystring (não POST).
  previewAlcada: (total: number) => api.get('/reembolso/preview-alcada', { params: { total } }).then((r) => r.data.data as PreviewAlcada),

  // previewCadeia (§11.6) — cadeia completa resolvida para o widget de steps.
  previewCadeia: (total: number) => api.get('/reembolso/preview-cadeia', { params: { total } }).then((r) => r.data.data as PreviewCadeia),

  /** Com `solicitacaoId`: CCs/itens do rateio e CC padrão do SOLICITANTE do pedido (conferente editando pedido alheio). */
  getContextoFormulario: (solicitacaoId?: number | null) =>
    api.get('/reembolso/contexto-formulario', { params: solicitacaoId ? { solicitacao: solicitacaoId } : undefined })
      .then((r) => r.data.data as ContextoFormulario),

  // Aprovador
  listPendentes: (params?: { status?: string; busca?: string; departamento?: string }) =>
    api.get('/reembolso/aprovacoes', { params }).then((r) => r.data.data as ListaResult),
  getDetalhe: (id: number) => api.get(`/reembolso/aprovacoes/${id}`).then((r) => r.data.data as Reembolso),
  aprovar: (id: number, observacao?: string) => api.patch(`/reembolso/aprovacoes/${id}/aprovar`, { observacao }).then((r) => r.data.data as Reembolso),
  // §12 — motivo opcional para o aprovador; observação mín. 20 (validado server-side).
  reprovar: (id: number, observacao: string, motivo?: string) =>
    api.patch(`/reembolso/aprovacoes/${id}/reprovar`, { observacao, motivo }).then((r) => r.data.data as Reembolso),

  // Rev7 §13.2 — fila unificada (papel resolvido no backend)
  fila: () =>
    api.get('/reembolso/fila').then((r) => r.data.data as { papel: 'conferente' | 'aprovador'; items: FilaItem[]; kpis: FilaKpis }),
  // Rev7 §13.1 — conferente marca o pedido como pago (botão da Task 6)
  pagar: (id: number) => api.post(`/reembolso/${id}/pagar`).then((r) => r.data.data as Reembolso),
  /**
   * Spec 2026-08-31 (Task 8) — conferente envia ao ERP fora da janela configurada.
   * Backend exige justificativa com 10..500 caracteres (trim), 403 se não for
   * conferente, 400 se o pedido não estiver `aprovado` ou já enviado.
   */
  enviarAgora: (id: number, justificativa: string) =>
    api.post(`/reembolso/${id}/enviar-agora`, { justificativa }).then((r) => r.data.data as Reembolso),

  // Finalização Fase 1 (Task 3) — contagens para o sino/badge do menu (aprovador/conferente).
  badgeCounts: () => api.get('/reembolso/badge-counts').then((r) => r.data.data as { aprovacoes: number; conferencias: number }),

  // §12 — Conferência do financeiro
  souConferente: () => api.get('/reembolso/sou-conferente').then((r) => r.data.data as { isConferente: boolean }),
  listarConferencias: () => api.get('/reembolso/conferencias').then((r) => r.data.data as ListaResult),
  getConferencia: (id: number) => api.get(`/reembolso/conferencias/${id}`).then((r) => r.data.data as Reembolso),
  conferirAprovar: (id: number, observacao?: string) =>
    api.patch(`/reembolso/conferencias/${id}/aprovar`, { observacao }).then((r) => r.data.data as Reembolso),
  conferirReprovar: (id: number, observacao: string, motivo?: string) =>
    api.patch(`/reembolso/conferencias/${id}/reprovar`, { observacao, motivo }).then((r) => r.data.data as Reembolso),

  // Central do financeiro
  centralFila: (f: FiltrosFilaCentral) =>
    api.get('/reembolso/financeiro/fila', { params: f }).then((r) => r.data.data as FilaCentral),
  pagarLote: (ids: number[]) =>
    api.post('/reembolso/pagar-lote', { ids }).then((r) => r.data.data as { resultados: ResultadoPagarLote[]; pagos: number; falhas: number }),
  marcarConferida: (solicitacaoId: number, despesaId: number, conferida: boolean) =>
    api.patch(`/reembolso/${solicitacaoId}/despesas/${despesaId}/conferida`, { conferida })
      .then((r) => r.data.data as { id: number; conferida_por_id: number | null; conferida_em: string | null }),
  // Reenvio: usar o `reenviarProtheus(id)` JÁ existente (admin da configuração) — não há `centralReenviar`.

  // Config (admin)
  getConfig: () => api.get('/reembolso/configuracoes').then((r) => r.data.data as ReembolsoConfig),
  saveConfig: (payload: SaveConfigPayload) => api.put('/reembolso/configuracoes', payload).then((r) => r.data.data as ReembolsoConfig),

  // Colaborador — revisar e histórico
  revisar: (id: number) => api.patch(`/reembolso/${id}/revisar`).then((r) => r.data.data as Reembolso),
  historico: (id: number) => api.get(`/reembolso/${id}/historico`).then((r) => r.data.data as HistoricoEntry[]),

  // Delegações
  listarDelegacoes: () => api.get('/reembolso/delegacoes').then((r) => r.data.data as Delegacao[]),
  criarDelegacao: (payload: CreateDelegacaoPayload) => api.post('/reembolso/delegacoes', payload).then((r) => r.data.data as Delegacao),
  desativarDelegacao: (id: number) => api.delete(`/reembolso/delegacoes/${id}`).then((r) => r.data.data as Delegacao),

  // Protheus integration
  getProtheusConfig: () => api.get('/reembolso/protheus/config').then((r) => r.data.data),
  saveProtheusConfig: (payload: unknown) => api.put('/reembolso/protheus/config', payload).then((r) => r.data.data),
  getProtheusFila: (params?: { status?: string; tenant?: string }) =>
    api.get('/reembolso/protheus/fila', { params }).then((r) => r.data.data as any[]),
  reenviarProtheus: (id: number) => api.post(`/reembolso/protheus/fila/${id}/reenviar`).then((r) => r.data.data),
  testProtheusConnection: (instancia: 'via' | 'sim'): Promise<IntegrationTestResult> =>
    api.post('/reembolso/protheus/test-connection', { instancia }).then((r) => r.data.data),
};

export default reembolsoApi;
